import React from 'react';
import { getDay, format } from 'date-fns';
import store from 'store/index';
import api from 'api';

import {
    setEvaluationResults,
    setDayEvaluationResults,
    setDayEvaluationResultsNk,
} from 'actions/index';

function getLang() {
    let lang = localStorage.getItem('zofapp_lang');
    if (!lang) lang = 'cs';
    if (!['cs', 'sk'].includes(lang)) lang = 'cs';
    return lang;
}

function isCurrentTaskSeminarWork() {
    const state = store.getState();
    const seminarWorkData = state.appModeData?.lectureInfoData?.seminar_work_data;
    return typeof seminarWorkData === 'object';
}

function isCurrentTaskSeminarWorkAssignment() {
    const state = store.getState();
    const seminarWorkData = state.appModeData?.lectureInfoData?.seminar_work_data;
    return typeof seminarWorkData === 'object' && seminarWorkData.is_assignment === 1;
}

function getLectureTaskMealCount(source) {
    let lectureData = null;

    if (source && typeof source === 'object') {
        if (source.lecture_data && typeof source.lecture_data === 'object') {
            lectureData = source.lecture_data;
        } else if (source.appModeData?.lectureInfoData?.lecture_data) {
            lectureData = source.appModeData.lectureInfoData.lecture_data;
        }
    }

    if (!lectureData) {
        const state = store.getState();
        lectureData = state.appModeData?.lectureInfoData?.lecture_data;
    }

    const mealTypes = Array.isArray(lectureData?.meal_types) ? lectureData.meal_types : [];
    return mealTypes.filter(mt => {
        const mealTypeId = parseInt(mt?.type, 10);
        return Number.isFinite(mealTypeId) && mealTypeId > 0;
    }).length;
}

function isSingleDishLectureTask(source) {
    return getLectureTaskMealCount(source) === 1;
}

function getLectureTaskDishTerm(source, form = 'nom', capitalize = false) {
    const lang = getLang();
    const isSingleDish = isSingleDishLectureTask(source);

    const terms = isSingleDish
        ? {
            cs: { nom: 'recept', acc: 'recept', gen: 'receptu', loc: 'receptu', ins: 'receptem' },
            sk: { nom: 'recept', acc: 'recept', gen: 'receptu', loc: 'recepte', ins: 'receptom' },
        }
        : {
            cs: { nom: 'jídelníček', acc: 'jídelníček', gen: 'jídelníčku', loc: 'jídelníčku', ins: 'jídelníčkem' },
            sk: { nom: 'jedálniček', acc: 'jedálniček', gen: 'jedálnička', loc: 'jedálničku', ins: 'jedálničkom' },
        };

    const langTerms = terms[lang] || terms.cs;
    let result = langTerms[form] || langTerms.nom;

    if (capitalize && result.length > 0) {
        result = result.charAt(0).toUpperCase() + result.slice(1);
    }

    return result;
}

function isMealDrinkRegime(m) {
    m = m.split('|');
    m = m[0] * 1;
    if (m === 20) return true;
    if (m >= 50 && m <= 69) return true;
    return false;
}

function isLectureLikeMode(appMode) {
    return appMode === 'LECTURE' || appMode === 'NUTRIKUCHAR';
}

function isNutriKucharMode(appMode) {
    return appMode === 'NUTRIKUCHAR';
}

function isReadOnly() {
    const state = store.getState();
    if (typeof state.readonly !== 'undefined') return state.readonly;
    return false;
}

function getSessionIdentifier() {
    const state = store.getState();
    if (typeof state.sessionIdentifier !== 'undefined') return state.sessionIdentifier;
    return '';
}

function apiRoot() {
    const appMode = localStorage.getItem('zofapp_mode');
    if (appMode === 'NUTRIKUCHAR') return 'https://www.zofapp.cz/api-nk';
    return 'https://www.zofapp.cz/api2';
}

function saveCurrentRecipeLocal() {
    const state = store.getState();
    const unsavedRecipe = {
        'foodGroups': state.foodGroups,
        'foods': state.foods,
        'titles': state.titles,
        'descriptions': state.descriptions,
    };
    localStorage.setItem('zof_unsaved_recipe', JSON.stringify(unsavedRecipe));
}

function saveDayPlan(callback) {
    const state = store.getState();

    let saveData = {};
    saveData.profile_id = state.profileId;
    saveData.date = JSON.stringify(state.plannerDate);
    saveData.titles = JSON.stringify(state.titles);
    saveData.meals = JSON.stringify(state.meals);
    saveData.groups = JSON.stringify(state.foodGroups);
    saveData.foods = JSON.stringify(state.foods);
    saveData.modifications = JSON.stringify(state.modifications);
    saveData.energyOutputSettings = JSON.stringify(state.energyOutputSettings);
    saveData.activities = JSON.stringify(state.activities);
    saveData.trainings = JSON.stringify(state.trainings);
    saveData = JSON.stringify(saveData);

    api('save/dayplan', { post: true, data: saveData }).then(result => {
        result = result.data;
        callback(result);
    });
}

function doLoadEvaluation(profileId) {
    if (!profileId || profileId === 'undefined') {
        const state = store.getState();
        profileId = state.autoEvaluateProfileId;
    }

    let state = store.getState();
    let stateSnapshot = Object.assign({}, state);
    stateSnapshot.selectedProfileId = profileId;
    delete stateSnapshot.globals;
    delete stateSnapshot.modalContent;
    delete stateSnapshot.evaluationResults;

    api('evaluation/meal', { post: true, data: stateSnapshot }).then(result => {
        result = result.data;
        store.dispatch(setEvaluationResults({
            'results': result.evaluation,
            'ddd': result['ddd']
        }));
    });
}

function nutritionRegimeIdToTagId(regimeId) {
    // ATTN: make dynamic
    const convert = {
        '1': 103,
        '10': 93,
        '20': 95,
        '30': 94,
        '40': 96,
        '50': 91,
        '60': 92,
    };

    regimeId = '' + regimeId;
    if (typeof convert[regimeId] === 'undefined') return 0;
    return convert[regimeId];
}

function readCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    if (match) return match[2];
    return false;
}

function writeCookie(name, value, expiryDays) {
    let expiry = new Date();
    expiry.setDate(expiry.getDate() + expiryDays);
    document.cookie = name + '=' + value + '; expires=' + expiry.toUTCString() + '; path=/';
}

function userHasPurchased(itemKey) {
    const state = store.getState();
    if (!state.globals.purchases) return false;
    return itemKey in state.globals.purchases;
}

function doLoadDayEvaluation(profileId, callback) {
    let state = store.getState();

    if (!profileId || profileId === 'undefined') {
        profileId = Object.keys(state.globals.profiles)[0];
    }

    let stateSnapshot = Object.assign({}, state);
    stateSnapshot.selectedProfileId = profileId;

    if (isLectureLikeMode(state.appMode)) {
        const fpid = getFirstProfileId();
        stateSnapshot.lectureTaskProfileId = state.globals.profiles[fpid].id;
    }

    delete stateSnapshot.globals;
    delete stateSnapshot.modalContent;
    delete stateSnapshot.evaluationResults;

    api('evaluation/menu', { post: true, data: stateSnapshot }).then(result => {
        result = result.data;
        store.dispatch(setDayEvaluationResults({
            'results': {
                'results': result['evaluation'],
                'ddd': result['ddd']
            },
            'mealsResults': result['meals_evaluation']
        }));
        if (callback) callback();
    });

    if (isNutriKucharMode(state.appMode)) {
        api('evaluation/recipe', { post: true, data: stateSnapshot }).then(result => {
            result = result.data;
            store.dispatch(setDayEvaluationResultsNk({
                'results': {
                    'excess': result['excess'],
                    'required': result['required'],
                    'results': result['results'],
                },
            }));
        });
    }
}

function getActiveProfileId() {
    const state = store.getState();
    let activeProfileId = state.profileId;
    if (activeProfileId === 0) {
        activeProfileId = Object.keys(state.globals.profiles)[0];
    }
    return activeProfileId;
}

// ATTN: This is fu**ing UGLY, the app should know which profile
// to use at which time! (mainly in LECTURE mode)!!!
function getOtherProfileId(currentProfileId) {
    const state = store.getState();
    let activeProfileId = state.profileId;
    if (activeProfileId === 0) {
        Object.keys(state.globals.profiles).map(id => {
            if (id !== currentProfileId) {
                activeProfileId = id;
            }
        });
    }
    return activeProfileId;
}

/*
 * Get first profile ID
 *
 * Skips any LectureTask-based profiles, ie. first profile must be user's profile
 */
function getFirstProfileId() {
    const state = store.getState();
    let primaryProfileId = -1;
    let firstProfileId = -1;

    let lectureTaskProfileId = -1;
    if (typeof state.appModeData.profileId === 'number') {
        lectureTaskProfileId = state.appModeData.profileId;
    }

    Object.keys(state.globals.profiles).map(profileId => {
        const profile = state.globals.profiles[profileId];
        if (profileId === lectureTaskProfileId) return;
        if (firstProfileId < 0) firstProfileId = profileId;
        if (profile.is_primary == 1 && primaryProfileId < 0) {
            primaryProfileId = profileId;
        }
    });

    if (primaryProfileId > 0) return primaryProfileId;
    if (firstProfileId > 0) return firstProfileId;
    return 0;
}

function parseRecipeDescription(t) {
    if (t.trim() === '') return false;
    let result = [];
    let stepIndex = 0;
    let lines = t.split('\n');
    lines.forEach(line => {
        if (line.trim() === '') return;
        const mStep = line.match(/^([0-9]+)(\.|\:)([^\n]+)/i);
        if (mStep) {
            let chunkText = mStep[3].trim();
            const mPrep = chunkText.match(/^(\s)?Příprava(.*?)\:(.*?)$/);
            if (mPrep) {
                chunkText = <span><b>Příprava {mPrep[2]}</b>:{mPrep[3]}</span>;
            }
            stepIndex++;
            result.push(
                <div key={stepIndex} className="flex items-start gap-[0.65em] py-[0.4em] border-b border-[#f3f3f3] last:border-0">
                    <div className="shrink-0 flex items-center justify-center w-[1.5em] h-[1.5em] rounded-full bg-[#26AA52] text-white text-[0.7em] font-bold mt-[0.15em]">
                        {mStep[1]}
                    </div>
                    <div className="flex-1 text-[0.9em] leading-relaxed">{chunkText}</div>
                </div>
            );
        } else {
            stepIndex = 0;
            const mHeading = line.match(/^\*\*\*([^\n]+)/i);
            if (mHeading) {
                result.push(
                    <p key={line} className="font-semibold mt-[0.75em] mb-[0.25em] text-[0.85em] text-[#444] uppercase tracking-wide">{mHeading[1]}</p>
                );
            } else {
                result.push(
                    <p key={line} className="text-[0.9em] text-[#555] my-[0.3em] leading-relaxed">{line}</p>
                );
            }
        }
    });
    return result;
}

function resetCookbookFilter() {
    localStorage.removeItem('zof_cookbook_filters');
}

function resetFoodsFilter() {
    localStorage.removeItem('zof_foods_filters');
}

function getFormattedDate(date) {
    const dayNames = ['neděle', 'pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota'];
    let weekDay = getDay(date) * 1;
    return dayNames[weekDay] + ' ' + format(date, 'd. M. yyyy');
}

function yyyymmdd(date) {
    let yyyy = '' + date.getFullYear();
    let mm = ('0' + (date.getMonth() + 1));
    mm = mm.substr(mm.length - 2);
    let dd = ('0' + date.getDate());
    dd = dd.substr(dd.length - 2);
    return yyyy + '-' + mm + '-' + dd;
}

function getBMR(profile) {
    if (profile.individual_bmr > 0) return profile.individual_bmr;

    const males = ['m', 'ms', 'b1', 'b2', 'b3', 'b4'];
    const females = ['f', 'fs', 'fp', 'fbf', 'g1', 'g2', 'g3', 'g4'];

    let gender = false;
    if (males.includes(profile.persona_key)) gender = 'm';
    if (females.includes(profile.persona_key)) gender = 'f';

    if (!gender) return 0;

    if (typeof profile.age === 'undefined') {
        let birth = new Date(profile.birthday);
        let cur = new Date();
        let diff = cur - birth;
        profile.age = Math.floor(diff / (1000 * 60 * 60 * 24 * 365));
    }

    let bmr = 0;
    if (gender == 'm') bmr = Math.ceil(10 * profile.weight + 6.25 * profile.height - 5 * profile.age + 5);
    if (gender == 'f') bmr = Math.ceil(10 * profile.weight + 6.25 * profile.height - 5 * profile.age - 161);
    return bmr;
}

function pieChart(data, cx, cy, radius, colours) {
    let chartelem = [];
    let max = data.length;
    let sum = 0;

    data.map((val) => { sum += val; });

    let deg = sum / 360;
    if (deg == 0) deg = .01;
    let jung = sum / 2;

    let dx = radius;
    let dy = 0;
    let oldangle = 0;

    for (let i = 0; i < max; i++) {
        let angle = oldangle + data[i] / deg;
        let x = Math.cos(angle * Math.PI / 180.0) * radius;
        let y = Math.sin(angle * Math.PI / 180.0) * radius;
        let colour = colours[i];
        let laf = 0;
        if (data[i] > jung) laf = 1;

        let ax = cx + x;
        let ay = cy + y;
        let adx = cx + dx;
        let ady = cy + dy;

        let d = [`M${cx},${cy} L${adx},${ady} A${radius},${radius} 0 ${laf},1 ${ax},${ay} z`];
        chartelem.push(<path d={d} fill={colour} stroke="black" strokeWidth={1} fillOpacity={1} key={i} />);
        dx = x;
        dy = y;
        oldangle = angle;
    }

    return chartelem;
}

function setPageTitle(title) {
    document.title = title + ' | ZOFapp';
}

function nl2br(text) {
    return text.split('\n').map(function (item, key) {
        return (
            <span key={key}>
                {item}
                <br />
            </span>
        );
    });
}

String.prototype.capitalize = function () {
    return this.charAt(0).toUpperCase() + this.slice(1);
};

const Util = {
    getLang,
    isCurrentTaskSeminarWork,
    isCurrentTaskSeminarWorkAssignment,
    getLectureTaskMealCount,
    isSingleDishLectureTask,
    getLectureTaskDishTerm,
    isMealDrinkRegime,
    isLectureLikeMode,
    isNutriKucharMode,
    isReadOnly,
    getSessionIdentifier,
    apiRoot,
    saveCurrentRecipeLocal,
    saveDayPlan,
    doLoadEvaluation,
    nutritionRegimeIdToTagId,
    readCookie,
    writeCookie,
    userHasPurchased,
    doLoadDayEvaluation,
    getActiveProfileId,
    getOtherProfileId,
    getFirstProfileId,
    parseRecipeDescription,
    resetCookbookFilter,
    resetFoodsFilter,
    getFormattedDate,
    yyyymmdd,
    getBMR,
    pieChart,
    setPageTitle,
    nl2br,
};

export default Util;
